import { computed, ref } from 'vue'

import type { MacroEditorAction, MacroRecordStatus } from '../types'
import { MACRO_DELAY_MAX } from '../types'

export type DraftRecordItem = MacroEditorAction & {
  __id?: string
  __status?: MacroRecordStatus
  __placeholder?: boolean
}

const deepClone = <T>(value: T): T => JSON.parse(JSON.stringify(value))

/**
 * 相邻延迟累加为一条。
 * 设备协议中延迟依附于前一个动作，连续延迟只会保留一条，
 * 编辑期间允许多条独立存在，落库/回显时在此归一，避免时长丢失。
 */
const mergeAdjacentDelays = <T extends MacroEditorAction>(list: T[]): T[] => {
  return list.reduce<T[]>((merged, item) => {
    const previous = merged[merged.length - 1]

    if (item.type === 4 && previous?.type === 4) {
      merged[merged.length - 1] = {
        ...previous,
        time: Math.min(previous.time + item.time, MACRO_DELAY_MAX),
      }
      return merged
    }

    merged.push(item)
    return merged
  }, [])
}

/**
 * 管理编辑区三层数据：
 * - originalData: 最近一次“已保存”快照
 * - workingData: 当前可编辑草稿
 * - displayData: workingData 过滤 deleted 后的展示数据
 */
export function useDraftDataManager(initialList: MacroEditorAction[] = []) {
  const originalData = ref<MacroEditorAction[]>([])
  const workingData = ref<DraftRecordItem[]>([])

  const idSeed = ref(0)
  const generateRecordId = () => `macro_record_${Date.now()}_${++idSeed.value}`

  const normalizeAsOriginal = (list: MacroEditorAction[]): DraftRecordItem[] => {
    return mergeAdjacentDelays(deepClone(list)).map((item) => ({
      ...item,
      __status: 'original',
      __id: generateRecordId(),
    }))
  }

  const resetOriginal = (list: MacroEditorAction[]) => {
    originalData.value = mergeAdjacentDelays(deepClone(list))
    workingData.value = normalizeAsOriginal(list)
  }

  // 初始化一次，保证组件首次渲染就有可用数据结构。
  resetOriginal(initialList)

  const displayData = computed(() =>
    workingData.value.filter((item) => item.__status !== 'deleted'),
  )

  const hasDraft = () => {
    if (workingData.value.some((item) => item.__status === 'deleted')) return true

    const visibleCount = workingData.value.filter((item) => item.__status !== 'deleted').length
    if (visibleCount !== originalData.value.length) return true

    return workingData.value.some(
      (item) => item.__status === 'modified' || item.__status === 'added',
    )
  }

  const clearDraft = () => {
    resetOriginal(originalData.value)
  }

  const clearWorking = () => {
    workingData.value = []
  }

  const getCleanData = (): MacroEditorAction[] => {
    const visibleData = workingData.value
      .filter((item) => item.__status !== 'deleted' && !item.__placeholder)
      .map((item) => {
        const cloned = deepClone(item) as DraftRecordItem
        delete cloned.__id
        delete cloned.__status
        delete cloned.__placeholder
        return cloned
      })

    return mergeAdjacentDelays(visibleData)
  }

  const saveRecords = () => {
    const cleanData = getCleanData()
    originalData.value = deepClone(cleanData)
    workingData.value = normalizeAsOriginal(cleanData)
    return cleanData
  }

  const findIndexById = (id?: string) => workingData.value.findIndex((item) => item.__id === id)
  const findIndexByRef = (record: DraftRecordItem) =>
    workingData.value.findIndex((item) => item === record)

  const insertAt = (index: number, record: DraftRecordItem) => {
    workingData.value.splice(index, 0, {
      ...record,
      __status: record.__status ?? 'added',
      __id: record.__id ?? generateRecordId(),
    })
  }

  const updateAt = (index: number, patch: Partial<DraftRecordItem>) => {
    if (index < 0 || index >= workingData.value.length) return false

    const next = {
      ...workingData.value[index],
      ...patch,
    }

    if (next.__status === 'original') {
      next.__status = 'modified'
    }

    workingData.value[index] = next as DraftRecordItem
    return true
  }

  const markAsDeleted = (index: number) => {
    if (index < 0 || index >= workingData.value.length) return false

    workingData.value[index] = {
      ...workingData.value[index],
      __status: 'deleted',
    }
    return true
  }

  return {
    originalData,
    workingData,
    displayData,
    generateRecordId,
    hasDraft,
    clearDraft,
    clearWorking,
    getCleanData,
    saveRecords,
    resetOriginal,
    findIndexById,
    findIndexByRef,
    insertAt,
    updateAt,
    markAsDeleted,
  }
}
